/**
 * EmergencyTable — real-time emergency alerts table.
 * Source: activeAlarms from NotificationContext.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { durationToNow, durationBetween } from '../../../../libs.jsx';
import AlertStatusBadge from './AlertStatusBadge.jsx';
import AlertTypeBadge from './AlertTypeBadge.jsx';

/** Hook that ticks every second to force duration re-renders */
function useTick(intervalMs = 1000) {
    const [, setTick] = useState(0);
    useEffect(() => {
        const timer = setInterval(() => setTick((t) => t + 1), intervalMs);
        return () => clearInterval(timer);
    }, [intervalMs]);
}

const OVERDUE_MS = 5 * 60 * 1000;
const CRITICAL_MS = 10 * 60 * 1000;

const getDurationColor = (status, eventStart) => {
    if (status !== 'Overdue' || !eventStart) return undefined;
    const elapsed = Date.now() - new Date(eventStart.endsWith?.('Z') ? eventStart : eventStart + 'Z').getTime();
    if (elapsed > CRITICAL_MS) return 'var(--color-danger, #e63757)';
    if (elapsed > OVERDUE_MS) return 'var(--color-warning-text, #d97706)';
    return undefined;
};

/** Duration cell — green when cleared, yellow/red when overdue, popover for response time */
const DurationCell = ({ eventStart, ackTime, status }) => {
    const [open, setOpen] = useState(false);
    const [style, setStyle] = useState({});
    const ref = useRef(null);
    const hasAck = Boolean(ackTime);

    const handleEnter = () => {
        if (!hasAck || !ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const popoverWidth = 260;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceRight = window.innerWidth - rect.left;
        const horizontal = spaceRight < popoverWidth + 16
            ? { right: `${window.innerWidth - rect.right}px`, left: 'auto' }
            : { left: `${rect.left}px`, right: 'auto' };
        const vertical = spaceBelow < 80
            ? { bottom: `${window.innerHeight - rect.top + 6}px`, top: 'auto' }
            : { top: `${rect.bottom + 6}px`, bottom: 'auto' };
        setStyle({ position: 'fixed', ...horizontal, ...vertical });
        setOpen(true);
    };

    const color = hasAck
        ? 'var(--color-success, #0cce6b)'
        : status === 'Accepted'
            ? 'var(--color-primary, #0081C7)'
            : getDurationColor(status, eventStart);

    return (
        <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={() => setOpen(false)}>
            <span className={`na-duration${hasAck ? ' na-duration--has-popover' : ''}`} style={color ? { color } : undefined}>
                {durationToNow(eventStart)}
            </span>
            {open && hasAck ? (
                <div className="na-status-popover" style={style}>
                    <strong>Response Time:</strong> {durationBetween(eventStart, ackTime)}
                </div>
            ) : null}
        </span>
    );
};

const EmergencyTable = ({ data, sortColumn, sortOrder, onSort, tzOffset }) => {
    useTick(1000); // Re-render every second for live duration updates

    const sortIcon = (col) => {
        if (sortColumn !== col) return null;
        return <span style={{ marginLeft: 4, fontSize: 10 }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>;
    };

    const formatUtcToLocal = (utcStr) => {
        if (!utcStr) return '—';
        try {
            const d = new Date(utcStr.endsWith('Z') ? utcStr : utcStr + 'Z');
            return d.toLocaleString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
        } catch { return utcStr; }
    };

    const renderResidents = (row) => {
        const residents = row.ResidentsObject;
        if (!residents || !Array.isArray(residents) || residents.length === 0) return '—';
        return residents.map((r, i) => (
            <span key={i}>
                {r.resident_id ? (
                    <Link to={`/profiles/resident/${r.resident_id}`} className="ar-link">
                        {r.firstName || r.fName || ''} {r.lastName || r.lName || ''}
                    </Link>
                ) : (`${r.firstName || r.fName || ''} ${r.lastName || r.lName || ''}`)}
                {i < residents.length - 1 ? ', ' : ''}
            </span>
        ));
    };

    return (
        <div className="dp-table-wrap" style={{ overflowX: 'auto' }}>
            <table className="dp-table">
                <thead className="dp-thead">
                    <tr>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('EventDateTime')}>Event Time{sortIcon('EventDateTime')}</th>
                        <th className="dp-th">Duration</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('AlertType')}>Type{sortIcon('AlertType')}</th>
                        <th className="dp-th">Residents</th>
                        <th className="dp-th">Device / Serial</th>
                        <th className="dp-th">Location / Area</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('NearName')}>Near{sortIcon('NearName')}</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('AcceptUsername')}>Accepted{sortIcon('AcceptUsername')}</th>
                        <th className="dp-th" style={{ cursor: 'pointer' }} onClick={() => onSort('Status')}>Status{sortIcon('Status')}</th>
                    </tr>
                </thead>
                <tbody>
                    {data.length === 0 ? (
                        <tr className="dp-tr">
                            <td className="dp-td" colSpan={9}>
                                <div className="na-empty">
                                    <div className="na-empty__icon">✓</div>
                                    <div className="na-empty__text">No active emergency alerts</div>
                                </div>
                            </td>
                        </tr>
                    ) : null}
                    {data.map((row) => (
                        <tr key={row.AlarmId} className="dp-tr na-row-border" style={{ borderLeftColor: row.AlertType === 'Emergency' ? '#e63757' : row.AlertType === 'Maintenance' ? '#ffc107' : '#0081C7' }}>
                            <td className="dp-td" style={{ whiteSpace: 'nowrap' }}>{formatUtcToLocal(row.EventDateTime)}</td>
                            <td className="dp-td">
                                <DurationCell eventStart={row.EventDateTime} ackTime={row.AckDateTime} status={row.Status} />
                            </td>
                            <td className="dp-td">
                                <AlertTypeBadge alertType={row.AlertType} alertSubType={row.AlertSubType} />
                            </td>
                            <td className="dp-td">{renderResidents(row)}</td>
                            <td className="dp-td">
                                <div className="na-stack">
                                    {row.DeviceId ? (
                                        <Link to={`/profiles/inovonics/${row.DeviceId}`} className="ar-link">{row.DeviceUse || '—'}</Link>
                                    ) : (<span>{row.DeviceUse || '—'}</span>)}
                                    <span className="na-stack__sub">{row.SerialNumber || ''}</span>
                                </div>
                            </td>
                            <td className="dp-td">
                                <div className="na-stack">
                                    {row.LocationId ? (
                                        <Link to={`/profiles/area/${row.LocationId}`} className="ar-link">{row.LocationName || '—'}</Link>
                                    ) : (<span>{row.LocationName || '—'}</span>)}
                                    <span className="na-stack__sub">{row.LocationArea || ''}</span>
                                </div>
                            </td>
                            <td className="dp-td">{row.NearName || '—'}</td>
                            <td className="dp-td">{row.AcceptUsername || '—'}</td>
                            <td className="dp-td"><AlertStatusBadge status={row.Status} eventDateTime={row.EventDateTime} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default EmergencyTable;
